import { qs, on } from '../../core/dom';
import type { NavModuleInit } from '../../core/types';

const EXPANDED_WIDTH = 275;
const COLLAPSED_WIDTH = 65;
const MEDIA_QUERY = '(min-width: 960px)';

function applyState(expanded: boolean): void {
  const container = document.querySelector<HTMLElement>('#side-nav-ssr, .f1-sidenav-container');
  const sidenav = document.querySelector<HTMLElement>('.f1-sidenav');
  const mainContent = document.getElementById('f1-main-right-content');
  const chevron = document.querySelector<SVGElement>(
    'button[aria-label="Toggle sidebar"] svg, #toggleButton svg',
  );

  if (container) {
    container.classList.toggle('expanded', expanded);
    container.classList.toggle('collapsed', !expanded);
  }

  if (sidenav) {
    sidenav.classList.toggle('f1-sidenav--expanded', expanded);
    sidenav.classList.toggle('f1-sidenav--collapsed', !expanded);
    sidenav.classList.toggle('px-3', expanded);
    sidenav.classList.toggle('px-2', !expanded);
  }

  if (mainContent) {
    mainContent.classList.toggle('f1-main-right-content--expanded', expanded);
    mainContent.classList.toggle('f1-main-right-content--collapsed', !expanded);
  }

  if (chevron) {
    chevron.classList.toggle('rotate-180', !expanded);
  }

  document.documentElement.style.setProperty(
    '--main-right-content-padding',
    `${(expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH) + 1}px`,
  );
}

function highlightActive(): void {
  const path = location.pathname.replace(/\/$/, '') || '/';
  document.querySelectorAll<HTMLAnchorElement>('a.tree-item-link[href]').forEach((anchor) => {
    const treeItem = anchor.querySelector<HTMLElement>('.tree-item');
    if (!treeItem) return;
    const rawHref = anchor.getAttribute('href') ?? '';
    if (!rawHref || rawHref === '#' || rawHref.startsWith('javascript:')) {
      treeItem.classList.remove('tree-item--active');
      return;
    }
    let urlHost = '';
    let urlPath = rawHref;
    try {
      const u = new URL(anchor.href, location.origin);
      urlHost = u.host;
      urlPath = u.pathname.replace(/\/$/, '') || '/';
    } catch {}
    const sameHost = urlHost === '' || urlHost === location.host;
    treeItem.classList.toggle('tree-item--active', sameHost && urlPath === path);
  });
}

function activateTreeItem(anchor: HTMLAnchorElement, e: Event): void {
  const me = e as MouseEvent;
  if (me.metaKey || me.ctrlKey || me.shiftKey || (me.button !== undefined && me.button !== 0)) {
    return;
  }

  const treeItem = anchor.querySelector<HTMLElement>('.tree-item');
  if (!treeItem?.classList.contains('tree-item--has-children')) return;

  const sidenav = anchor.closest('.f1-sidenav');
  const isCollapsed = sidenav?.classList.contains('f1-sidenav--collapsed') ?? false;
  if (isCollapsed) return;

  e.preventDefault();

  const isExpanded = treeItem.classList.toggle('tree-item--expanded');
  anchor.setAttribute('aria-expanded', String(isExpanded));
  const children = anchor.parentElement?.querySelector<HTMLElement>(
    ':scope > [data-tree-children]',
  );
  children?.classList.toggle('tree-item-children--expanded', isExpanded);
}

function bindExpandClicks(): void {
  document.querySelectorAll<HTMLAnchorElement>('a.tree-item-link').forEach((anchor) => {
    if (anchor.dataset.gnTreeBound === '1') return;
    anchor.dataset.gnTreeBound = '1';
    anchor.addEventListener('click', (e) => activateTreeItem(anchor, e));
  });
}

const init: NavModuleInit = ({ bus }) => {
  const container = qs<HTMLElement>('#side-nav-ssr, .f1-sidenav-container');
  if (!container) return;

  highlightActive();
  bindExpandClicks();

  const isLargerThanMobile = window.matchMedia(MEDIA_QUERY).matches;
  const ssrExpanded = container.classList.contains('expanded');
  const initialExpanded = isLargerThanMobile && ssrExpanded;

  applyState(initialExpanded);

  const toggle = qs<HTMLButtonElement>('#toggleButton, button[aria-label="Toggle sidebar"]');

  const onClick = () => {
    if (!window.matchMedia(MEDIA_QUERY).matches) return;
    const sidenav = document.querySelector<HTMLElement>('.f1-sidenav');
    const isExpanded = sidenav?.classList.contains('f1-sidenav--expanded') ?? false;
    const next = !isExpanded;
    applyState(next);
    bus.emit('side-nav:expand', { expanded: next });
  };

  const offClick = toggle ? on(toggle, 'click', onClick) : () => undefined;

  const mql = window.matchMedia(MEDIA_QUERY);
  const onMediaChange = (e: MediaQueryListEvent) => {
    if (!e.matches) {
      applyState(false);
    }
  };
  mql.addEventListener('change', onMediaChange);

  return {
    destroy: () => {
      offClick();
      mql.removeEventListener('change', onMediaChange);
    },
  };
};

export default init;
